import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import {
  LayoutDashboard, Users, UserPlus, Calendar, Receipt, Megaphone, LogOut, Home, ShieldCheck, Mail, Plug, MessageSquareQuote, MessageCircle, Lock,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/admin", end: true, icon: LayoutDashboard, label: "Overview" },
  { to: "/admin/leads", icon: UserPlus, label: "Leads" },
  { to: "/admin/clients", icon: Users, label: "Clients" },
  { to: "/admin/messages", icon: MessageCircle, label: "Messages" },
  { to: "/admin/appointments", icon: Calendar, label: "Appointments" },
  { to: "/admin/invoices", icon: Receipt, label: "Invoices" },
  { to: "/admin/announcements", icon: Megaphone, label: "Announcements" },
  { to: "/admin/drips", icon: Mail, label: "Drip Sequences" },
  { to: "/admin/reviews", icon: MessageSquareQuote, label: "Testimonials" },
  { to: "/admin/integrations", icon: Plug, label: "Integrations" },
  { to: "/admin/team", icon: ShieldCheck, label: "Team", roles: ["admin"] },
  { to: "/admin/profile", icon: Lock, label: "Profile & Security" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const visibleNav = ADMIN_NAV.filter((n) => !n.roles || n.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-soft">
      <header data-testid="admin-header" className="sticky top-0 z-30 backdrop-blur-xl bg-navy text-soft border-b border-soft/10">
        <div className="container-app flex items-center justify-between py-4">
          <Link to="/admin" className="flex items-center gap-3">
            <Logo surface="dark" to={null} testid="admin-logo" size="h-10 md:h-11" />
            <span className="hidden md:inline-flex text-[10px] uppercase tracking-[0.25em] text-gold border-l border-soft/15 pl-3">
              {user?.role === "admin" ? "Admin Console" : "Team Console"}
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm text-soft/70 hover:text-soft">
              <Home className="w-4 h-4" /> Public site
            </Link>
            <div className="hidden sm:block leading-tight text-right">
              <div className="text-sm text-soft">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-widest text-soft/50">{user?.email}</div>
            </div>
            <button
              data-testid="admin-logout"
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-soft/70 hover:text-soft p-2"
              aria-label="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="container-app py-10 grid lg:grid-cols-12 gap-8">
        <aside className="lg:col-span-3" data-testid="admin-sidebar">
          <nav className="card p-2 lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto no-scrollbar">
            {visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-xl px-4 py-3 text-sm whitespace-nowrap transition ${
                    isActive ? "bg-navy text-soft" : "text-navy/70 hover:bg-navy/5"
                  }`
                }
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="lg:col-span-9">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
